import { useWatch } from 'react-hook-form'

import { SelectFormField } from '@/components/form/select-form-field'
import { Select } from '@/components/ui/select'
import { TextInput } from '@/components/ui/text-input'

import {
  SCHEDULE_END_MINUTES,
  SCHEDULE_START_MINUTES,
  TIME_OPTIONS,
} from './week-schedule.constants'
import * as S from './week-schedule.styled'
import type {
  WeekScheduleDay,
  WeekScheduleLabels,
  WeekScheduleVariant,
} from './week-schedule.types'

type DayHoursRowProps = {
  name: string
  index: number
  labels: WeekScheduleLabels
  variant: WeekScheduleVariant
  minDurationMinutes: number
}

export const DayHoursRow = ({
  name,
  index,
  labels,
  variant,
  minDurationMinutes,
}: DayHoursRowProps) => {
  const day = useWatch({ name: `${name}.${index}` }) as WeekScheduleDay | undefined

  const startTime = day?.startTime
  const endTime = day?.endTime
  // Note: only offer times that leave at least `minDurationMinutes` between start and end, so no
  // pick can produce a window the form would then reject.
  const latestStart = (endTime ?? SCHEDULE_END_MINUTES) - minDurationMinutes
  const earliestEnd = (startTime ?? SCHEDULE_START_MINUTES) + minDurationMinutes
  const startOptions = TIME_OPTIONS.filter((opt) => opt.value <= latestStart)
  const endOptions = TIME_OPTIONS.filter((opt) => opt.value >= earliestEnd)

  return (
    <S.DayHoursRow>
      <S.DayName $variant={variant}>
        <S.DayLabel size="text-medium" weight="medium" color="primary800">
          {day?.name}
        </S.DayLabel>
      </S.DayName>
      {day?.enabled ? (
        <S.TimeSelectors>
          <S.ControlWrapper>
            <SelectFormField
              name={`${name}.${index}.startTime`}
              placeholder={labels.startPlaceholder}
              options={startOptions}
            />
          </S.ControlWrapper>
          <S.TimeSeparator size="text-medium" weight="medium" color="primary800">
            {labels.to}
          </S.TimeSeparator>
          <S.ControlWrapper $stretch={variant === 'maintenance'}>
            <SelectFormField
              name={`${name}.${index}.endTime`}
              placeholder={labels.endPlaceholder}
              options={endOptions}
            />
          </S.ControlWrapper>
        </S.TimeSelectors>
      ) : (
        <S.ControlWrapper>
          {variant === 'maintenance' ? (
            <Select disabled placeholder="" options={[]} value={null} onChange={() => undefined} />
          ) : (
            <TextInput disabled readOnly value={labels.noHours} />
          )}
        </S.ControlWrapper>
      )}
    </S.DayHoursRow>
  )
}
